/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import DeleteRecordButton from "../input/DeleteRecordButton";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import AccessControl from "../functional/AccessControl";
import getSchema from "../../lib/api/getSchema";
import notificationState from "../../states/notificationState";
import UpdateRecordButton from "../input/UpdateRecordButton";
import getList from "../../lib/api/getList";
import CreateRecordButton from "../input/CreateRecordButton";
import CustomSelector from "../input/CustomSelector";

/**
 * @COMPONENT_NAME CommonCodeTEST
 * @DESCRIPTION 공통코드 관련 폼 입력과 필터 폼 입력 테스트를 위한 화면입니다.
 *
 * @PROPS
 *    - {Object} selectedmst - 선택한 마스터 그리드 데이터 Object
	  - {Funtion} setInitialDataCallback - select에서 선택한 mstCodeId 값 초기화를 위한 콜백 함수 
 * @CALLBACKS
 *    - {String} viewSchemaUrl - 스키마 변경 api url
 *    - {Function} onModifyViewUrl - view 변경 콜백함수
 *	  - {Function} onApiInitialized - API 초기화 시 호출되는 콜백 함수
 *	  - {Function} onFailure - 등록, 수정, 삭제 실패 시 호출되는 콜백 함수
 *	  - {Function} onNotifyWarning - 등록, 수정, 삭제 시 경고 트리거 시 경고 알림 콜백 함수
 *	  - {Function} onSuccess - 등록, 수정, 삭제 시 호출되는 콜백 함수
 */
function CommonCodeTEST(props) {
	const { t } = useTranslation();
	const table = "CommonCodeTEST";
	const view = "VwCommonCodeTEST";

	const [schema, setSchema] = useState();
	const [grid, setGrid] = useState();
	const [api, setApi] = useState();
	const [selectedSlave, setSlectedSlave] = useState();
	const [mstArray, setMstArray] = useState([]);
	const [selectedMstCode, setSelectedMstCode] = useState("A001");
	const viewSchemaUrl = `/api/CommonCodeTESTSchema/${selectedMstCode}`;
	useEffect(() => {
		(async () => {
			let response = await getList("CommonCodeMst");
			const list = Array.isArray(response) ? response : response?.data;
			if (Array.isArray(list)) {
				const selected = list.map((item) => item.mstCodeId);
				setMstArray(selected);
			} else {
				console.warn("Expected array but got:", response);
				setMstArray([]);
			}
		})();
	}, []);

	useEffect(() => {
		let mounted = true;

		async function schemaSetting() {
			let schema = await getSchema(table);

			if (mounted) {
				setSchema(schema);
			}
		}

		schemaSetting();
		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		if (grid) {
			grid.selectionChanged.addHandler((grid, event) => {
				if (grid.selectedItems && grid.selectedItems[0]) {
					setSlectedSlave(grid.selectedItems[0]);
				} else {
					setSlectedSlave();
				}
			});

			grid.itemsSourceChanged.addHandler((grid, event) => {
				if (grid.selectedItems && grid.selectedItems[0]) {
					setSlectedSlave(grid.selectedItems[0]);
				} else {
					setSlectedSlave();
				}
			});
		}
	}, [grid]);

	useEffect(() => {
		if (api) api.refresh();
	}, [schema]);

	// 스키마가 오기 전에 상위 코드를 고르면 하위 코드 목록이 기본(A001)으로 남았다 — 스키마가 온 뒤에도 고른 코드를 입힌다
	useEffect(() => {
		if (!schema) return;

		const updatedSchema = {
			...schema,
			columns: schema.columns.map((col) => {
				if (col.name === "slaveCodeId") {
					return {
						...col,
						commonCodeInfo: {
							...(col.commonCodeInfo || {}),
							mstCodeId: selectedMstCode,
							nameColumn: ["slaveCodeId", "slaveCodeName"],
							valueColumn: "slaveCodeId",
							useEmptyValue: true,
							emptyName: "term.none",
							sortByName: true,
						},
					};
				}
				return col;
			}),
		};

		setSchema(updatedSchema);
	}, [selectedMstCode, Boolean(schema)]);

	const setNotification = useSetRecoilState(notificationState);

	const onApiInitialized = (api) => {
		setApi(api);
	};

	const onGridInitialized = (grid) => {
		grid.selectionMode = "Row";
		setGrid(grid);
	};

	const notifySuccess = (msg) => {
		setNotification({
			open: true,
			severity: "success",
			msg: msg,
		});
	};

	const onSuccess = (msg) => {
		notifySuccess(msg);
	};

	const notifyFailure = (msg) => {
		setNotification({
			open: true,
			severity: "error",
			msg: msg,
		});
	};

	const onFailure = (reason) => {
		notifyFailure(reason);
	};

	const notifyWarning = (msg) => {
		setNotification({
			open: true,
			severity: "warning",
			msg: msg,
		});
	};

	const onNotifyWarning = (msg) => {
		notifyWarning(msg);
	};
	const setInitialDataCallback = (formData) => {
		if (selectedMstCode) {
			formData["mstCodeId"] = selectedMstCode;
		}
	};
	return (
		<BasicCrudScreenType
			id={table}
			headerTitle={t("term.commonCodeTEST")}
			table={table}
			view={view}
			viewSchemaUrl={viewSchemaUrl}
			noCreate
			noEdit
			noDelete
			noExcel
			onInitialized={onGridInitialized}
			onApiInitialized={onApiInitialized}
			onAppendButtonInFrontOfSearch={() => (
				<>
					{/* 고르기 칸은 줄 하나를 차지해 같은 칸에 두면 버튼이 목록 밑으로 밀려 가려진다 — 따로 둔다 */}
					<BasicCrudScreenActionItemLayout>
						<CustomSelector
							value={selectedMstCode}
							setValue={setSelectedMstCode}
							apiArray={mstArray}
						/>
					</BasicCrudScreenActionItemLayout>
					<BasicCrudScreenActionItemLayout>
						<AccessControl
							accessTarget={schema && schema.name}
							accessMethod="POST"
						>
							<CreateRecordButton
								id={table}
								schema={schema}
								refresh={api}
								onSuccess={onSuccess}
								setInitialDataCallback={setInitialDataCallback}
							/>
						</AccessControl>

						<AccessControl
							accessTarget={schema && schema.name}
							accessMethod={
								schema &&
								(schema.updateWithPatch ? "PATCH" : "PUT")
							}
						>
							<UpdateRecordButton
								id={table}
								schema={schema}
								grid={grid}
								refresh={api}
								onSuccess={onSuccess}
								disabled={!selectedSlave}
							/>
						</AccessControl>

						<AccessControl
							accessTarget={schema && schema.name}
							accessMethod="DELETE"
						>
							<DeleteRecordButton
								id={table}
								schema={schema}
								grid={grid}
								refresh={api}
								disabled={!selectedSlave}
								color="secondary"
								onSuccess={onSuccess}
								onFailure={onFailure}
								onPartialFailure={onNotifyWarning}
							/>
						</AccessControl>
					</BasicCrudScreenActionItemLayout>
				</>
			)}
		/>
	);
}

export default CommonCodeTEST;
